import {
  HandleRegenerateOptionsType,
  type HandleSubmitMessageType,
  type HandleSubmitOptionsType,
} from '@/utils/aiUtils';
import { useChat } from '@ai-sdk/react';
import { useAuth } from '@clerk/clerk-react';
import { type DtUIMessage, type StandardsChatContext } from '@ditto/shared';
import { DefaultChatTransport } from 'ai';
import { useAuthenticatedClient, useWorkspaceBaseUrl } from './useApi';
import { useMutation } from '@tanstack/react-query';

export function useStandardsChatWithAuthHeaders(
  getChatContext: () => StandardsChatContext
) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const workspaceBaseUrl = useWorkspaceBaseUrl();

  const {
    sendMessage: sendMessageWithoutAuth,
    regenerate: regenerateWithoutAuth,
    ...rest
  } = useChat<DtUIMessage>({
    resume: true,
    experimental_throttle: 100,
    transport: new DefaultChatTransport({
      prepareReconnectToStreamRequest: async () => {
        const token = await getToken();
        const chatContext = getChatContext();
        const chatId = chatContext.id;
        return {
          api: `${workspaceBaseUrl}/standards/chat/${chatId}/stream`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
      prepareSendMessagesRequest({ messages, body }) {
        return {
          api: `${workspaceBaseUrl}/standards/chat`,
          body: {
            ...body,
            message: messages[messages.length - 1],
          },
        };
      },
    }),
  });

  const sendMessage = async (
    message: HandleSubmitMessageType,
    opts?: HandleSubmitOptionsType
  ) => {
    if (!isLoaded || !isSignedIn) {
      throw new Error('User is not authenticated');
    }

    const token = await getToken();

    const chatContext = getChatContext();

    sendMessageWithoutAuth(message, {
      ...opts,
      body: {
        ...opts?.body,
        chatContext,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        ...opts?.headers,
      },
    });
  };

  const regenerate = async (opts?: HandleRegenerateOptionsType) => {
    if (!isLoaded || !isSignedIn) {
      throw new Error('User is not authenticated');
    }

    const token = await getToken();

    const chatContext = getChatContext();

    regenerateWithoutAuth({
      ...opts,
      body: {
        ...opts?.body,
        chatContext,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        ...opts?.headers,
      },
    });
  };

  return { sendMessage, regenerate, ...rest };
}

export const useCreateStandardsChatApi = () => {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async () => {
      const response = await client.standards.createChat({
        body: {},
      });

      if (response.status === 200) {
        return response.body;
      }

      throw new Error('Failed to create standards chat');
    },
  });
};
